import { autobind } from "core-decorators";
import { Inject } from "typescript-ioc";
import { DeliveryChannel } from "./DeliveryChannel";
import { UiComponentFactory } from "Ui/Scripts/UiComponentFactory";
import { EventBinder } from "Events/Scripts/EventBinder";
import { LoggerFactory } from "Logging/Scripts/LoggerFactory";
import { Utils } from "Helpers/Scripts/Utils";
import { IChannelUpdateEvent } from "./IChannelUpdateEvent";
import { ISelectedChannelAddress } from "./ISelectedChannelAddress";
import { IMultipleCouriersToFriendModel } from "./IMultipleCouriersToFriendModel";
import { FetchService } from "Async/Scripts/FetchService";
import { DeliveryNotificationsPhone } from "./DeliveryNotificationsPhone";
import { Div } from "Ui/Scripts/Div";
import { IMultipleCouriersAddressModel } from "./IMultipleCouriersAddressModel";
import { IEndConsumerAddressChangeEvent } from "./IEndConsumerAddressChangeEvent";
import * as DeliveryChannelsEventType from "./DeliveryChannelsEventType";
import { EndConsumerSelectorComponent } from "EndConsumerSelector/Scripts/EndConsumerSelectorComponent";
import { ShippingOffer } from "./ShippingOffer/ShippingOffer";

export class MultipleCouriersToFriendChannel extends DeliveryChannel {
    // Others
    private readonly MULTIPLE_COURIERS_STOCK_INFO_TEMPLATE: string =
        "multiple-couriers-info-template";
    private readonly MULTIPLE_COURIERS_DELIVERY_DATE_TEMPLATE: string =
        "multiple-couriers-delivery-date-template";
    private readonly KENDO_SELECTED_CLASS: string = "k-state-selected";
    private readonly SELECTED_CLASS: string = "selected";
    private readonly DISPLAY_ATTRIBUTE: string = "display";
    private readonly METHOD_RADIO_ID: string = "multipleCouriersToFriend";
    private readonly METHOD_LABEL_ID = "multipleCouriersToFriendLabel";
    private readonly DELIVERY_DATE: string = "js-delivery-date";
    private readonly AJAX_ERROR_TITLE: string = "Error";
    private readonly AJAX_ERROR_MESSAGE: string =
        "An error occured during request, please try again later.";
    private readonly ADDRESS_DATA_ATTRIBUTE: string = "data-address";
    private readonly CHANNEL_CHECKED_CLASS: string = "channel-checked";
    private readonly ADDRESS_ID_DATA_ATTRIBUTE: string = "data-address-id";
    private readonly COURIER_DESCRIPTION_CLASS: string = "channel-details__description";
    private readonly COURIER_IMAGE_CLASS: string = "channel-details__logo";
    private readonly COURIER_DETAILS: string = "channel-details";
    private readonly SRCSET_ATTRIBUTES: string = "&h=20&imageFormat=jpeg 20w";
    private readonly COURIERS_LIST_TEMPLATE: string = "multiple-couriers-to-friend-channels";
    private readonly ACTIVE_SHOWN_DATA_ATTRIBUTE: string = "data-active-shown";
    private readonly ACTIVE_HIDDEN_DATA_ATTRIBUTE: string = "data-active-hidden";
    private readonly ADD_FRIEND_CLASS: string = "js-add-freind";
    private readonly MESSAGE_SWITCHER: string = "js-message-switcher";
    public static readonly SELECT_CHANNEL_EVENT: string = "selectmultiplecourierchannelevent";

    // Selectors
    private readonly DELIVERY_PAGE_SELECTOR: string = "#delivery-page";
    private readonly INPUT_SELECTOR: string = "input";
    private readonly END_CONSUMER_ADDRESS_BUTTONS_SELECTOR: string =
        this.INPUT_SELECTOR + "[name^='end-consumer-address-radio']";
    private readonly METHOD_RADIO_SELECTOR: string = "#" + this.METHOD_RADIO_ID;
    private readonly COURIER_WRAPPER_SELECTOR: string = ".courier-wrapper";
    private readonly VISIBLE_COURIERS_SELECTOR: string = this.COURIER_WRAPPER_SELECTOR + ":visible";
    private readonly SELECTED_COURIER_SELECTOR: string =
        this.COURIER_WRAPPER_SELECTOR + "." + this.SELECTED_CLASS;
    private readonly DELIVERY_COMMENT_SELECTOR: string = "#friend-delivery-comment";
    private readonly DELIVERY_TO_FRIEND_CONTAINER_SELECTOR: string =
        ".js-delivery-to-friend-container";
    private readonly COURIERS_CONTAINER_SELECTOR: string = ".js-friend-couriers-container";
    private readonly ADDRESS_ITEM_SELECTOR: string = ".js-address-item";
    private readonly CHECKED_SELECTOR: string = ":checked";
    private readonly ENABLED_SELECTOR: string = ":enabled";
    private readonly UPDATE_OPERATION_BUTTON_SELECTOR: string = ".js-update-operation-button";
    private readonly DELIVERY_COMMENT_WRAPPER_SELECTOR: string = ".js-delivery-comment";
    private readonly ADDRESSES_WRAPPER_SELECTOR: string =
        ".js-multiple-couriers-to-friend-addresses";
    private readonly KENDO_TEMPLATE_PICKUP_MESSAGE: string = "delivery-message-template";
    public readonly END_CONSUMER_COURIERS_SELECTOR: string =
        ".js-end-consumer-selector-content-container .courier-wrapper__info";

    // Data keys
    private readonly UPDATE_DELIVERY_COMMENT_DATA_KEY: string = "updateDeliveryCommentUrl";
    private readonly END_CONSUMER_DATA_KEY: string = "endConsumerSelectorUrl";
    private readonly ADDRESS_ID_DATA_KEY: string = "addressId";
    private readonly CLEAR_DELIVERY_ADDRESS_DATA_KEY: string = "clearDeliveryAddressUrl";

    private readonly _fetchService: FetchService;
    public static readonly templateId: string = "multiple-couriers-to-friend-template";
    public static readonly componentName: string = "MultipleCouriersToFriendChannel";

    private _selectedDeliveryAddressId: number | null = null;
    private _updateDeliveryCommentUrl: string;
    private _deliveryNotificationsPhone: DeliveryNotificationsPhone;
    private _endConsumerSelectorUrl: string;
    private _endConsumerSelectorComponent: EndConsumerSelectorComponent;
    protected _model: IMultipleCouriersToFriendModel;
    private _shippingOffer: ShippingOffer | null = null;
    private _clearDeliveryAddressUrl: string;
    private _wasNewAddressAdded: boolean = false;

    constructor(
        @Inject componentFactory: UiComponentFactory,
        @Inject binder: EventBinder,
        @Inject loggerFactory: LoggerFactory,
        @Inject fetchService: FetchService
    ) {
        super(componentFactory, binder, loggerFactory);

        this._fetchService = fetchService;
    }

    public get key(): string {
        return "MultipleCouriersToFriendChannel";
    }

    private get _couriresListTemplate(): (data: IMultipleCouriersAddressModel) => string {
        return Utils.getTemplate(this.COURIERS_LIST_TEMPLATE);
    }

    private get _multipleCouriersStockInfoTemplate(): (data: {}) => string {
        return Utils.getTemplate(this.MULTIPLE_COURIERS_STOCK_INFO_TEMPLATE);
    }

    private get _multipleCouriersDeliveryDateTemplate(): (data: IChannelsInfo) => string {
        return Utils.getTemplate(this.MULTIPLE_COURIERS_DELIVERY_DATE_TEMPLATE);
    }

    public set shippingOffer(shippingOfferInstance: ShippingOffer) {
        this._shippingOffer = shippingOfferInstance;
    }

    public init(): void {
        this._initRadioButton(this.METHOD_RADIO_SELECTOR);
        this._setUrls();
        this._initDeliveryComment();
        this._initEndConsumerComponent();
        if (
            this._model.HomeDeliveryPhoneNumbers &&
            this._model.HomeDeliveryPhoneNumbers.NotificationsPhones
        ) {
            this._deliveryNotificationsPhone = this.createComponent(
                DeliveryNotificationsPhone,
                this.DELIVERY_NOTIFICATIONS_PHONES_SELECTOR,
                this._model.HomeDeliveryPhoneNumbers
            );
            const phoneBox = this.findElement(this.DELIVERY_NOTIFICATIONS_PHONES_SELECTOR);
            if (phoneBox) {
                Utils.removeClass(phoneBox, this.CLASS_HIDDEN);
            }
        }

        this._forceDisplayInvalidAddresses(this._model.EndConsumersCourierDeliveryAddresses);
    }

    private _initEndConsumerComponent(): void {
        this._fetchService.get(this._endConsumerSelectorUrl).then(
            (response: string) => {
                this._logger.info("Delivery to a friend fetched successfully.");
                const deliveryToFriendContainer = this.createComponent(
                    Div,
                    this.DELIVERY_TO_FRIEND_CONTAINER_SELECTOR
                );
                if (deliveryToFriendContainer) {
                    deliveryToFriendContainer.setHtml(response);

                    if (ori && ori.endConsumerSelectorComponentFactory) {
                        this._endConsumerSelectorComponent =
                            ori.endConsumerSelectorComponentFactory.createAll(
                                deliveryToFriendContainer.context
                            )[0];
                    }
                }
                this._showCouriersContainers();
                this._initRadioButtons();
                this._disableFriendsEmptyAddresses();

                this._placeCouriersToAddresses();
                this._disableEmptyAddresses();
                this._enableAllowedAddresses();
                this._hideNotAllowedChannels();
                this._initAllOutOfStockTooltips();
                this._initAllUnsupportedAddressTooltips();
                this._bindEvents();
                this._handleShowEmptyAddressMessageInDeliveryToAFriend(
                    this._model.EndConsumersCourierDeliveryAddresses
                );

                if (
                    !this._model.isDefaultSelected &&
                    this._model.SelectedHomeDeliveryAddress &&
                    this._model.SelectedDeliveryChannel &&
                    this._containsSelected()
                ) {
                    this._initDeliveryMessage();
                    this._makeCourierSelected();
                    this._makeAddressSelected(this._model.SelectedHomeDeliveryAddress);
                    this._toggleHomeDeliverySelection(true);
                    this._handleShowEmptyAddressMessage(
                        this._model.EndConsumersCourierDeliveryAddresses
                    );
                    this._toggleFeesVisibility(this._model.SelectedHomeDeliveryAddress, false);
                    this._updateShippingOffers();
                }
            },
            (error) => {
                this._logger.error("Delivery to a friend fetch failed - %s", error);
            }
        );
    }

    private _placeCouriersToAddresses(): void {
        if (this._model.EndConsumersCourierDeliveryAddresses.length) {
            this._model.EndConsumersCourierDeliveryAddresses.forEach((address) => {
                const couriersContainer = this.findElement(
                    `[${this.ADDRESS_ID_DATA_ATTRIBUTE}="${address.Id}"]
                    ${this.COURIERS_CONTAINER_SELECTOR}`
                );
                if (couriersContainer) {
                    Utils.setHtml(couriersContainer, this._couriresListTemplate(address));
                }
            });
        }
    }

    private _setUrls(): void {
        const page = this.closest(this.DELIVERY_PAGE_SELECTOR);
        if (page) {
            this._endConsumerSelectorUrl = Utils.getData(
                page,
                this.END_CONSUMER_DATA_KEY
            ).endConsumerSelector;
            this._clearDeliveryAddressUrl = Utils.getData(
                page,
                this.CLEAR_DELIVERY_ADDRESS_DATA_KEY
            ).clearDeliveryAddressUrl;
            this._updateDeliveryCommentUrl = Utils.getData(
                page,
                this.UPDATE_DELIVERY_COMMENT_DATA_KEY
            ).updateDeliveryCommentUrl;
        }
    }

    private _initDeliveryMessage(): void {
        if (!this._model.Message) {
            return;
        }

        const endConsumerCourierInfo = document.querySelectorAll(
            this.END_CONSUMER_COURIERS_SELECTOR
        );

        if (endConsumerCourierInfo) {
            const messageTemplate = this._getPickupPointMessageTemplate();
            const messageElement = Utils.createDomElementFromMarkup(messageTemplate(this._model));

            if (messageElement) {
                endConsumerCourierInfo.forEach((item) => Utils.append(item, messageElement));
            }
        }
    }

    private _getPickupPointMessageTemplate(): (data: any) => string {
        return Utils.getTemplate(this.KENDO_TEMPLATE_PICKUP_MESSAGE);
    }

    private _initAllOutOfStockTooltips(): void {
        for (const address of this._model.EndConsumersCourierDeliveryAddresses) {
            this._initOutOfStockTooltips(address);
        }
    }

    private _initOutOfStockTooltips(address: IMultipleCouriersAddressModel): void {
        const friendAddressSelector = `[${this.ADDRESS_ID_DATA_ATTRIBUTE}="${address.Id}"]`;
        this._handleOutOfStockTooltip(address.OutOfStockItems, 0, friendAddressSelector);
    }

    private _initAllUnsupportedAddressTooltips(): void {
        for (const address of this._model.EndConsumersCourierDeliveryAddresses) {
            this._initUnsupportedAddressTooltips(address);
        }
    }

    private _initUnsupportedAddressTooltips(address: IMultipleCouriersAddressModel): void {
        const friendAddressSelector = `[${this.ADDRESS_ID_DATA_ATTRIBUTE}="${address.Id}"]`;
        this._handleUnsupportedAddressTooltip(0, friendAddressSelector);
    }

    private _toggleHomeDeliverySelection(isSelected: boolean): void {
        if (isSelected) {
            this._makeChannelSelected();
        } else {
            this._makeChannelUnselected();
        }
    }

    private _makeChannelUnselected(): void {
        const radioButton = kendo.getRadioButton(this.METHOD_RADIO_SELECTOR);
        if (radioButton) {
            radioButton.value(false);
        }
    }

    private _updateShippingOffers(): void {
        if (this._model && this._shippingOffer) {
            this._shippingOffer.update(this._model.SelectedDeliveryChannelModel);
        }
    }

    private _makeCourierSelected(deliveryChannel?: number): void {
        const selectedDeliveryChannel = deliveryChannel
            ? deliveryChannel
            : this._model.SelectedDeliveryChannel;

        const courier = this.findElement(
            `[${this.ADDRESS_ID_DATA_ATTRIBUTE}="${this._model.SelectedHomeDeliveryAddress}"]
                [${this.DELIVERY_CHANNEL_DATA_ATTRIBUTE}="${selectedDeliveryChannel}"]`
        );
        if (courier && !Utils.hasClass(courier, this.CLASS_HIDDEN)) {
            Utils.addClass(courier, this.SELECTED_CLASS);
        }
    }

    private _makeAddressSelected(addressId: number): void {
        const addressButton = this.findElement(`${this.INPUT_SELECTOR}[value="${addressId}"]`);
        if (addressButton && !addressButton.hasAttribute(this.DISABLED_ATTRIBUTE)) {
            const radioButton = kendo.getRadioButton(addressButton);
            if (radioButton) {
                radioButton.value(true);
                this._selectedDeliveryAddressId = addressId;
                this._showUpdateButtons();
            }
        }
    }

    private _containsSelected(): boolean {
        return this._model.EndConsumersCourierDeliveryAddresses.some(
            (address) =>
                address.Id === this._model.SelectedHomeDeliveryAddress &&
                address.HomeDeliveryChannels.some(
                    (channel) => channel.DeliveryChannelId === this._model.SelectedDeliveryChannel
                )
        );
    }

    private _triggerEventWhenLastAddresRemoved(
        previousAddresses: IMultipleCouriersAddressModel[],
        nextAddresses: IMultipleCouriersAddressModel[]
    ): void {
        if (
            previousAddresses.length > nextAddresses.length &&
            !nextAddresses.some((address) => address.isAllowed === true)
        ) {
            this._binder.trigger(MultipleCouriersToFriendChannel.SELECT_CHANNEL_EVENT);
        }
    }

    private _updateAddedAddressesFromModel(nextState: IMultipleCouriersToFriendModel): void {
        const addedAddresses = nextState.EndConsumersCourierDeliveryAddresses.slice(
            this._model.EndConsumersCourierDeliveryAddresses.length
        );

        for (const address of addedAddresses) {
            this._updateStockInfo(address);
            for (const channel of address.HomeDeliveryChannels) {
                this._updateDeliveryDate(channel, address.Id);
                this._updateCourierFee(channel.DeliveryFee, channel.DeliveryChannelId, address.Id);
            }
        }
    }

    public update(nextState: IMultipleCouriersToFriendModel): void {
        if (
            this._model.EndConsumersCourierDeliveryAddresses.length <
            nextState.EndConsumersCourierDeliveryAddresses.length
        ) {
            this._updateAddedAddressesFromModel(nextState);
        }

        let previousAddress;
        let nextAddress;

        let wasComponentUpdated = this._wasAddressListUpdated(
            this._model.EndConsumersCourierDeliveryAddresses,
            nextState.EndConsumersCourierDeliveryAddresses
        );

        if (wasComponentUpdated) {
            this._checkComponentUpdated(nextState);

            this._handleShowEmptyAddressMessage(nextState.EndConsumersCourierDeliveryAddresses);
            this._handleShowEmptyAddressMessageInDeliveryToAFriend(
                nextState.EndConsumersCourierDeliveryAddresses
            );
            this._endConsumerSelectorComponent.refresh(true);
            if (
                nextState.EndConsumersCourierDeliveryAddresses.length >
                this._model.EndConsumersCourierDeliveryAddresses.length
            ) {
                this._wasNewAddressAdded = true;
            }
        }

        for (let i = 0; i < this._model.EndConsumersCourierDeliveryAddresses.length; i++) {
            if (
                this._model.EndConsumersCourierDeliveryAddresses[i].Id ===
                nextState.SelectedHomeDeliveryAddress
            ) {
                previousAddress = this._model.EndConsumersCourierDeliveryAddresses[i];
                nextAddress = nextState.EndConsumersCourierDeliveryAddresses[i];
            }
            if (!nextAddress) {
                continue;
            }
        }
        if (nextAddress && previousAddress) {
            for (let j = 0; j < previousAddress.HomeDeliveryChannels.length; j++) {
                const previousChannel = previousAddress.HomeDeliveryChannels[j];
                const nextChannel = nextAddress.HomeDeliveryChannels[j];
                if (!nextChannel || nextChannel.IsAllowed === false) {
                    continue;
                }
                const wasChannelUpdated = this._isUpdatedFromDeliveryModelCheck(
                    previousChannel,
                    nextChannel
                );
                if (!wasComponentUpdated && wasChannelUpdated) {
                    wasComponentUpdated = true;
                }
                if (nextState.SelectedDeliveryChannel === nextChannel.DeliveryChannelId) {
                    nextAddress.OutOfStockItems = nextChannel.OutOfStockItems;
                    this._updateStockInfo(nextAddress);
                }
            }
            const checkedChannels = document.querySelectorAll(`.${this.CHANNEL_CHECKED_CLASS}`);
            checkedChannels.forEach((channel) => {
                channel.classList.remove(`${this.CHANNEL_CHECKED_CLASS}`);
            });
        }

        this._toggleFeesVisibility(this._selectedDeliveryAddressId, false);

        this._model = nextState;

        if (wasComponentUpdated) {
            this._disableEmptyAddresses();
            this._enableAllowedAddresses();
            this._hideNotAllowedChannels();
            this._forceDisplayInvalidAddresses(this._model.EndConsumersCourierDeliveryAddresses);
        }
    }

    private _isUpdatedFromDeliveryModelCheck(
        prevState: IChannelsInfo,
        nextState: IChannelsInfo
    ): boolean {
        if (!this._shouldComponentUpdate(prevState, nextState)) {
            const courier = document.querySelector(
                `${this.COURIER_WRAPPER_SELECTOR}:not(.${this.CHANNEL_CHECKED_CLASS})[${this.DATA_ADDRESS}='${this._selectedDeliveryAddressId}']`
            );
            if (courier) {
                courier.classList.add(this.CHANNEL_CHECKED_CLASS);
            }
            return false;
        }

        if (this._selectedDeliveryAddressId) {
            this._checkCourier(nextState, this._selectedDeliveryAddressId);
        }

        return true;
    }

    private _checkCourier(nextState: IChannelsInfo, addressId: number): void {
        const courier = document.querySelector(
            `${this.COURIER_WRAPPER_SELECTOR}:not(.${this.CHANNEL_CHECKED_CLASS})[${this.DATA_ADDRESS}='${addressId}']`
        );

        if (courier) {
            // eslint-disable-next-line @typescript-eslint/dot-notation
            const courierId = (courier as HTMLElement).dataset["deliveryChannel"];

            if (nextState.IsAllowed) {
                Utils.toggleClass(courier, this.CLASS_HIDDEN, !nextState.IsAllowed);
            }

            const courierDescription = document.querySelector(
                `${this.COURIER_WRAPPER_SELECTOR}:not(.${this.CHANNEL_CHECKED_CLASS})[${this.DATA_ADDRESS}='${addressId}']
                 .${this.COURIER_DESCRIPTION_CLASS}`
            );

            if (courierDescription) {
                Utils.setHtml(courierDescription, kendo.toString(nextState.Description, "oc"));
            }

            let courierImage = document.querySelector(
                `${this.COURIER_WRAPPER_SELECTOR}:not(.${this.CHANNEL_CHECKED_CLASS})[${this.DATA_ADDRESS}=
                '${addressId}'][${this.DATA_DELIVERY_CHANNEL}='${courierId}'] .${this.COURIER_IMAGE_CLASS}`
            );

            const courierImagePath = document.querySelector(
                `${this.COURIER_WRAPPER_SELECTOR}:not(.${this.CHANNEL_CHECKED_CLASS})[${this.DATA_ADDRESS}='${addressId}']
                 .${this.COURIER_DETAILS}`
            );

            if (nextState.Image) {
                if (!courierImage && courierImagePath) {
                    courierImage = document.createElement("img");
                    courierImage.classList.add(this.COURIER_IMAGE_CLASS);
                    courierImagePath.appendChild(courierImage);
                }

                if (courierImage) {
                    courierImage.setAttribute(
                        "srcset",
                        nextState.Image.Src + this.SRCSET_ATTRIBUTES
                    );
                    courierImage.setAttribute("alt", nextState.Image.Alt);
                }
            } else {
                if (courierImage) {
                    Utils.addClass(courierImage, this.CLASS_HIDDEN);
                }
            }

            if (nextState.DeliveryDate) {
                const deliveryDate = document.querySelector(
                    `${this.COURIER_WRAPPER_SELECTOR}:not(.${this.CHANNEL_CHECKED_CLASS})[${this.DATA_ADDRESS}='${addressId}']
                     .${this.DELIVERY_DATE}`
                );

                if (deliveryDate) {
                    Utils.setHtml(
                        deliveryDate,
                        this._multipleCouriersDeliveryDateTemplate(nextState)
                    );
                }
            }

            const deliveryFee = document.querySelector(
                `${this.COURIER_WRAPPER_SELECTOR}:not(.${this.CHANNEL_CHECKED_CLASS})[${this.DATA_ADDRESS}='${addressId}']
                 .${this.DELIVERY_FEE_CLASS}`
            );

            if (deliveryFee) {
                Utils.setHtml(deliveryFee, kendo.toString(nextState.DeliveryFee, "oc"));
            }

            const courierNextId = nextState.DeliveryChannelId.toString();
            courier.classList.add(this.CHANNEL_CHECKED_CLASS);
            courier.setAttribute(this.DELIVERY_CHANNEL_DATA_ATTRIBUTE, courierNextId);
        }
    }

    private _checkComponentUpdated(nextState: IMultipleCouriersToFriendModel): void {
        if (
            this._model.EndConsumersCourierDeliveryAddresses.length >
            nextState.EndConsumersCourierDeliveryAddresses.length
        ) {
            this._clearDeliveryAddressSelection();
            if (!nextState.EndConsumersCourierDeliveryAddresses.length) {
                this._toggleHomeDeliverySelection(false);
                this._forceDisplayInvalidAddresses(
                    this._model.EndConsumersCourierDeliveryAddresses
                );
            }
            this._triggerEventWhenLastAddresRemoved(
                this._model.EndConsumersCourierDeliveryAddresses,
                nextState.EndConsumersCourierDeliveryAddresses
            );
        } else if (
            this._model.EndConsumersCourierDeliveryAddresses.length <
            nextState.EndConsumersCourierDeliveryAddresses.length
        ) {
            this._toggleHomeDeliverySelection(true);
            this._forceDisplayInvalidAddresses(this._model.EndConsumersCourierDeliveryAddresses);
        }
    }

    private _updateStockInfo(address: IMultipleCouriersAddressModel): void {
        const homeAddressSelector = `[${this.ADDRESS_ID_DATA_ATTRIBUTE} = "${address.Id}"]`;
        const stockInfo = this.findElement(
            homeAddressSelector + ` .${this.DELIVERY_CHANNEL_INFO_CLASS}`
        );

        if (stockInfo) {
            const data = {
                address,
            };
            Utils.setHtml(stockInfo, this._multipleCouriersStockInfoTemplate(data));
            this._handleOutOfStockTooltip(address.OutOfStockItems, 0, homeAddressSelector);
        }
    }

    private _updateDeliveryDate(data: IChannelsInfo, addressId: number): void {
        const deliveryDate = this.findElement(
            `[${this.DELIVERY_CHANNEL_DATA_ATTRIBUTE}="${data.DeliveryChannelId}"][${this.DATA_ADDRESS}="${addressId}"]
                    .${this.DELIVERY_DATE}`
        );

        if (deliveryDate) {
            Utils.setHtml(deliveryDate, this._multipleCouriersDeliveryDateTemplate(data));
        }
    }

    private _updateCourierFee(fee: number, deliveryChannelId: number, addressId: number): void {
        const deliveryFee = this.findElement(
            `[${this.DELIVERY_CHANNEL_DATA_ATTRIBUTE}="${deliveryChannelId}"][${this.DATA_ADDRESS}="${addressId}"]
                    .${this.DELIVERY_FEE_CLASS}`
        );
        if (deliveryFee) {
            Utils.setHtml(deliveryFee, kendo.toString(fee, "oc"));
        }
    }

    private _bindEvents(): void {
        this.bindClick(this._handleClick);
        this._bindStepChange();
        this._bindChannelChange();
        this._bindCommentChange();
        this._binder.bindCustomEvent(
            DeliveryChannelsEventType.END_CONSUMER_ADDRESS_CHANGE,
            (event: IEndConsumerAddressChangeEvent) => {
                event.stopPropagation();
                this._handleAddressChange(event);
            }
        );
        this._binder.bindCustomEvent(
            DeliveryChannelsEventType.END_CONSUMER_COMPONENT_CHANGE,
            (event) => {
                event?.stopPropagation();
                this._handleComponentChange();
            }
        );
        this._binder.bindCustomEvent(DeliveryChannelsEventType.END_CONSUMER_REFRESH, (event) => {
            event?.stopPropagation();
            this._handleComponentRefresh();
        });
    }

    /**
     * Binds custom select courier channel event handler. Multiple calls will bind multiple event handlers
     * which will be executed in the order they were added.
     * @param handler Event handler method reference.
     * @param namespace Optional event namespace used when binding this particular handler via jQuery.
     * @param one When true, the handler will be disconnected after first execution.
     */
    public bindSelectCourierChannel(
        handler: GenericEventHandler,
        namespace?: string,
        one?: boolean
    ): this {
        this._binder.bindCustomEvent(
            MultipleCouriersToFriendChannel.SELECT_CHANNEL_EVENT,
            handler,
            namespace,
            one
        );

        return this;
    }

    /**
     * Disconnects custom select courier channel event handler or all select courier channel event handlers
     * when no particular handler reference was provided.
     * @param namespace Optional event namespace used when binding this particular handler via jQuery.
     * @param handler Optional event handler method reference.
     */
    public unbindSelectCourierChannel(namespace?: string, handler?: GenericEventHandler): this {
        this._binder.unbindCustomEvent(
            MultipleCouriersToFriendChannel.SELECT_CHANNEL_EVENT,
            namespace,
            handler
        );

        return this;
    }

    private _bindCommentChange(): void {
        const textBox = kendo.getTextBox(this.DELIVERY_COMMENT_SELECTOR);
        if (textBox) {
            textBox.bindChange(this._updateDeliveryComment);
        }
    }

    private _bindChannelChange(): void {
        const input = this.findElement(this.METHOD_RADIO_SELECTOR);
        if (input) {
            const radioButton = kendo.getRadioButton(input);
            if (radioButton) {
                radioButton.bindChange((event) => {
                    if (event && event.sender) {
                        this._removeAddressSelection();
                        this._triggerUpdateChannel(event.sender);
                    }
                });
            }
        }
    }

    @autobind
    private _triggerUpdateChannel(homeDeliveryRadio: kendo.ui.RadioButton): void {
        if (homeDeliveryRadio.value()) {
            const data: ISelectedChannelAddress = {
                deliveryAddress: null,
                deliveryChannel: null,
            };

            this._triggerChannelUpdate(data);
        }
    }

    @autobind
    private _removeAddressSelection(): void {
        this._removePreviousCourierSelection();
        this._deselectAddress();
    }

    private _deselectAddress(): void {
        const addressButton = this.findElement(
            this.END_CONSUMER_ADDRESS_BUTTONS_SELECTOR + this.CHECKED_SELECTOR
        );
        if (addressButton) {
            const radioButton = kendo.getRadioButton(addressButton);
            if (radioButton) {
                radioButton.value(false);
                radioButton.wrapper.removeClass(this.KENDO_SELECTED_CLASS);
                radioButton.wrapper
                    .siblings(this.COURIERS_CONTAINER_SELECTOR)
                    .css(this.DISPLAY_ATTRIBUTE, "");
            }
        }
    }

    // Trigger updateShoppingContext to trigger update from deliveryModel
    @autobind
    private _handleComponentChange(): void {
        if (ori && ori.shopping && ori.shopping.wizard) {
            const shoppingContext = ori.shopping.wizard.getShoppingContext();
            ori.shopping.wizard.updateShoppingContext(
                shoppingContext,
                ori.shopping.wizard.deliveryPageId,
                false
            );
        }
    }

    @autobind
    private _handleComponentRefresh(): void {
        this._showCouriersContainers();
        this._initRadioButtons();
        this._disableFriendsEmptyAddresses();

        this._placeCouriersToAddresses();
        this._disableEmptyAddresses();
        this._enableAllowedAddresses();
        this._hideNotAllowedChannels();
        this._initAllOutOfStockTooltips();
        this._initAllUnsupportedAddressTooltips();
        this._forceDisplayInvalidAddresses(this._model.EndConsumersCourierDeliveryAddresses);

        if (this._wasNewAddressAdded) {
            this._toggleHomeDeliverySelection(true);
            const endConsumersLength = this._model.EndConsumersCourierDeliveryAddresses.length - 1;
            this._selectAddress(
                this._model.EndConsumersCourierDeliveryAddresses[endConsumersLength].Id
            );
            this._wasNewAddressAdded = false;
        } else if (this._model.SelectedHomeDeliveryAddress) {
            this._selectAddress(this._model.SelectedHomeDeliveryAddress);
        }
    }

    @autobind
    private _handleAddressChange(event: IEndConsumerAddressChangeEvent): void {
        if (event.deliveryAddressId) {
            const data: ISelectedChannelAddress = {
                deliveryAddress: null,
                deliveryChannel: null,
                preventReloadModules: true,
            };

            this._handleAddressClick(event.deliveryAddressId, data);
            this._selectFirstCourier(event.deliveryAddressId);
        }
    }

    @autobind
    private _handleClick(event: IChannelUpdateEvent): void {
        if (Utils.hasClass(event.target, this.ADD_FRIEND_CLASS)) {
            this._endConsumerSelectorComponent.onCreate();

            return;
        }

        if (event.target.id === this.METHOD_LABEL_ID) {
            if (
                !this._model.EndConsumersCourierDeliveryAddresses.length &&
                this._model.IsAddEndConsumerAddressEnabled
            ) {
                event.preventDefault();
                this._endConsumerSelectorComponent.onCreate();
                return;
            }
        }

        if (event.target.id === this.METHOD_RADIO_ID) {
            if (
                this._model.HomeDeliveryPhoneNumbers &&
                !this._model.HomeDeliveryPhoneNumbers.SelectedNotificationsPhoneId &&
                this._deliveryNotificationsPhone
            ) {
                this._deliveryNotificationsPhone.clearPhoneDropDownListSelection();
            }

            const enabledAddress = this.findElement(
                `${this.ADDRESS_ITEM_SELECTOR} ${this.INPUT_SELECTOR}${this.ENABLED_SELECTOR}`
            );
            if (enabledAddress) {
                const enabledAddressId = Number((enabledAddress as HTMLInputElement).value);
                this._selectAddress(enabledAddressId);
            }

            this._handleShowEmptyAddressMessage(this._model.EndConsumersCourierDeliveryAddresses);
            this._handleShowEmptyAddressMessageInDeliveryToAFriend(
                this._model.EndConsumersCourierDeliveryAddresses
            );
        }

        const clickedCourier = this._getClickedCourier(event.target);

        if (clickedCourier && !Utils.getAttr(clickedCourier, this.DISABLED_ATTRIBUTE)) {
            const data: ISelectedChannelAddress = {
                deliveryAddress: null,
                deliveryChannel: null,
                isCourierSelection: true,
                preventReloadModules: false,
            };

            this._handleCourierClick(clickedCourier, data);
        }
    }

    private _handleAddressClick(nextAddressId: number, data: ISelectedChannelAddress): void {
        if (this._selectedDeliveryAddressId !== nextAddressId) {
            this._toggleFeesVisibility(this._selectedDeliveryAddressId, true);
            this._selectedDeliveryAddressId = nextAddressId;
            data.deliveryAddress = this._selectedDeliveryAddressId;
            this._removePreviousCourierSelection();
            this._triggerChannelUpdate(data);
        }
    }

    private _handleCourierClick(clickedCourier: Element, data: ISelectedChannelAddress): void {
        const deliveryChannelId = Utils.getAttr(clickedCourier, this.DATA_DELIVERY_CHANNEL);

        if (deliveryChannelId) {
            data.deliveryChannel = Number(deliveryChannelId);
            data.deliveryAddress = this._selectedDeliveryAddressId;
            this._removePreviousCourierSelection();
            Utils.addClass(clickedCourier, this.SELECTED_CLASS);
            if (data.deliveryAddress !== null) {
                this._triggerChannelUpdate(data);
            }
        }
    }

    private _selectFirstCourier(addressId: number): void {
        const couriers = this.find(
            `[${this.ADDRESS_ID_DATA_ATTRIBUTE}="${addressId}"] ` + this.COURIER_WRAPPER_SELECTOR
        );

        const data: ISelectedChannelAddress = {
            deliveryAddress: null,
            deliveryChannel: null,
            isCourierSelection: true,
            preventReloadModules: false,
        };

        if (couriers) {
            this._handleCourierClick(couriers[0], data);
        } else {
            data.deliveryAddress = this._selectedDeliveryAddressId;
            this._triggerChannelUpdate(data);
        }
    }

    private _selectAddress(addressId: number): void {
        const data: ISelectedChannelAddress = {
            deliveryAddress: null,
            deliveryChannel: null,
            isCourierSelection: true,
            preventReloadModules: true,
        };

        this._handleAddressClick(addressId, data);
        this._makeAddressSelected(addressId);
        this._selectFirstCourier(addressId);
    }

    private _removePreviousCourierSelection(): void {
        const previousCourier = this.findElement(this.SELECTED_COURIER_SELECTOR);
        if (previousCourier) {
            Utils.removeClass(previousCourier, this.SELECTED_CLASS);
        }
    }

    private _getClickedCourier(innerElement: Element): Element | null {
        const couriers = this.find(this.VISIBLE_COURIERS_SELECTOR);
        for (const courier of couriers) {
            if (courier.contains(innerElement)) {
                return courier;
            }
        }
        return null;
    }

    private _bindStepChange(): void {
        if (!ori) {
            this._logger.error("Ori namespace is not available");
            return;
        }

        if (!ori.shopping || !ori.shopping.wizard) {
            this._logger.error("Shopping wizard is not available");
            return;
        }
        ori.shopping.wizard.on(ori.shopping.wizard.shoppingContextChangeEvent(), () => {
            this._updateDeliveryComment();
        });
    }

    private _hideNotAllowedChannels(): void {
        for (const address of this._model.EndConsumersCourierDeliveryAddresses) {
            for (const channel of address.HomeDeliveryChannels) {
                this._toggleChannelDisplay(channel, address.Id);
            }
        }
    }

    private _toggleChannelDisplay(channel: IChannelsInfo, addressId: number): void {
        const courier = this.findElement(
            `[${this.DELIVERY_CHANNEL_DATA_ATTRIBUTE}="${channel.DeliveryChannelId}"][${this.ADDRESS_DATA_ATTRIBUTE}="${addressId}"]`
        );

        if (courier) {
            Utils.toggleClass(courier, this.CLASS_HIDDEN, !channel.IsAllowed);
        }
    }

    private _enableAllowedAddresses(): void {
        this._model.EndConsumersCourierDeliveryAddresses.forEach((item) => {
            const input = this.findElement(`${this.INPUT_SELECTOR}[value=${item.Id}]`);
            if (input) {
                this._enableAllowedAddress(input as HTMLInputElement, Boolean(item.isAllowed));
            }
        });

        if (
            !this._model.EndConsumersCourierDeliveryAddresses.length &&
            this._model.IsAddEndConsumerAddressEnabled
        ) {
            this._enableFriendDelivery(true);
            return;
        }

        const notAllowedAddresses = this._model.EndConsumersCourierDeliveryAddresses.filter(
            (x) => !x.isAllowed
        );
        const isAllowedAddressExist =
            notAllowedAddresses.length !== this._model.EndConsumersCourierDeliveryAddresses.length;

        this._enableFriendDelivery(isAllowedAddressExist);
    }

    private _disableFriendsEmptyAddresses(): void {
        const emptyAddresses = this.find(
            `${this.ADDRESS_ITEM_SELECTOR}[${this.ADDRESS_ID_DATA_ATTRIBUTE}=''] ${this.INPUT_SELECTOR}`
        );
        emptyAddresses.forEach((address) => this._enableAddress(address, false));
    }

    private _enableAllowedAddress(input: HTMLInputElement, isEnabled: boolean): void {
        this._enableAddress(input, isEnabled);
        const couriers = this.find(
            `[${this.DELIVERY_CHANNEL_DATA_ATTRIBUTE}][${this.DATA_ADDRESS}="${input.value}"]`
        );
        if (couriers) {
            couriers.forEach((courier) => {
                if (!isEnabled) {
                    courier.setAttribute(this.DISABLED_ATTRIBUTE, this.DISABLED_ATTRIBUTE);
                } else {
                    courier.removeAttribute(this.DISABLED_ATTRIBUTE);
                }
            });
        }
    }

    private _enableAddress(input: Element, isEnabled: boolean): void {
        const radioButton = kendo.getRadioButton(input);
        if (radioButton) {
            radioButton.enable(isEnabled);
        }
    }

    private _enableFriendDelivery(isEnabled?: boolean): void {
        const radioButton = kendo.getRadioButton(this.METHOD_RADIO_SELECTOR);
        if (radioButton) {
            radioButton.enable(isEnabled);
        }
    }

    private _initRadioButtons(): void {
        const addressButtons = this.find(this.END_CONSUMER_ADDRESS_BUTTONS_SELECTOR);
        if (addressButtons) {
            for (const addressButton of addressButtons) {
                this._initRadioButton(addressButton);
            }
        }
    }

    private _initDeliveryComment(): void {
        const input = this.findElement(this.DELIVERY_COMMENT_SELECTOR);
        if (input) {
            kendo.createTextBox(input);
        }
    }

    @autobind
    private _updateDeliveryComment(): void {
        const input = kendo.getTextBox(this.DELIVERY_COMMENT_SELECTOR);
        if (input) {
            const comment = input.value();
            if (comment) {
                const data = new FormData();
                data.append("deliveryComment", comment);
                this._fetchService.post(this._updateDeliveryCommentUrl, data).then(
                    () => {
                        this._logger.info("Delivery comment posted successfully.");
                    },
                    (error) => {
                        this._logger.error("Delivery comment post failed - %s", error);
                        if (ori.shopping) {
                            ori.displayError(
                                this.AJAX_ERROR_TITLE,
                                ori.shopping.getAjaxErrorMessage()
                            );
                        } else {
                            ori.displayError(this.AJAX_ERROR_TITLE, this.AJAX_ERROR_MESSAGE);
                        }
                    }
                );
            }
        }
    }

    private _disableEmptyAddresses(): void {
        const allAddresses = this.find(this.ADDRESS_ITEM_SELECTOR);
        if (allAddresses) {
            const addressesToDisable = allAddresses.filter(
                (address) => !Utils.find(address, this.COURIER_WRAPPER_SELECTOR).length
            );

            addressesToDisable.forEach((address) => {
                const addressId = Utils.getData(address, this.ADDRESS_ID_DATA_KEY);
                if (addressId) {
                    const input = this.findElement(`input[value=${addressId}]`);
                    if (input) {
                        this._enableAllowedAddress(input as HTMLInputElement, false);
                    }
                }
            });

            this._enableFriendDelivery(
                addressesToDisable.length !==
                    this._model.EndConsumersCourierDeliveryAddresses.length
            );
        }
    }

    private _toggleFeesVisibility(addressId: number | null, hide: boolean): void {
        const deliveryFees = this.find(
            `[${this.ADDRESS_ID_DATA_ATTRIBUTE}="${addressId}"]
            ${this.VISIBLE_COURIERS_SELECTOR} .${this.DELIVERY_FEE_CLASS}`
        );

        if (deliveryFees) {
            deliveryFees.forEach((fee) => Utils.toggleClass(fee, this.CLASS_HIDDEN, hide));
        }
    }

    private _showUpdateButtons(): void {
        const updateOperationsButtons = this.find(
            `[${this.ADDRESS_ID_DATA_ATTRIBUTE}="${this._selectedDeliveryAddressId}"]
                 ${this.UPDATE_OPERATION_BUTTON_SELECTOR} `
        );

        if (updateOperationsButtons) {
            for (const button of updateOperationsButtons) {
                Utils.removeClass(button, this.CLASS_HIDDEN);
            }
        }
    }

    private _showCouriersContainers(): void {
        const couriersContainers = this.find(`${this.COURIERS_CONTAINER_SELECTOR}`);
        if (couriersContainers.length) {
            couriersContainers.forEach((courierContainer) =>
                Utils.removeClass(courierContainer, this.CLASS_HIDDEN)
            );
        }
    }

    private _wasAddressListUpdated(
        previousAddresses: IMultipleCouriersAddressModel[],
        nextAddresses: IMultipleCouriersAddressModel[]
    ): boolean {
        if (previousAddresses.length !== nextAddresses.length) {
            return true;
        }
        const propertiesHolder: IAddress = {
            Address: "",
            Caption: "",
            City: "",
            HomeDeliveryChannelsInfo: [],
            Id: 0,
            Province: "",
            isAllowed: false,
        };

        return Object.keys(propertiesHolder).some(() =>
            previousAddresses.some(
                (previousAddress, index) =>
                    previousAddress.Address !== nextAddresses[index].Address ||
                    previousAddress.Caption !== nextAddresses[index].Caption ||
                    previousAddress.City !== nextAddresses[index].City ||
                    this._wasChannelsListUpdated(
                        previousAddress.HomeDeliveryChannelsInfo,
                        nextAddresses[index].HomeDeliveryChannelsInfo
                    ) ||
                    previousAddress.Id !== nextAddresses[index].Id ||
                    previousAddress.Province !== nextAddresses[index].Province ||
                    previousAddress.isAllowed !== nextAddresses[index].isAllowed
            )
        );
    }

    private _wasChannelsListUpdated(
        previousChannels: IChannelAddressInfo[],
        nextChannels: IChannelAddressInfo[]
    ): boolean {
        if (previousChannels.length !== nextChannels.length) {
            return true;
        }

        return previousChannels.some(
            (previousChannel, index) =>
                previousChannel.Id !== nextChannels[index].Id ||
                previousChannel.IsAllowed !== nextChannels[index].IsAllowed
        );
    }

    private _handleShowEmptyAddressMessageInDeliveryToAFriend(
        endConsumerAddresses: IMultipleCouriersAddressModel[]
    ): void {
        const messageSwitcher = this.findElement(`.${this.MESSAGE_SWITCHER}`);
        if (!messageSwitcher) {
            return;
        }

        const allowedAddresses = endConsumerAddresses.filter((address) => address.isAllowed);
        messageSwitcher.removeAttribute("style");
        messageSwitcher.classList.toggle(this.CLASS_HIDDEN, Boolean(allowedAddresses.length));
    }

    private _handleShowEmptyAddressMessage(
        endConsumerAddresses: IMultipleCouriersAddressModel[]
    ): void {
        const allowedAddresses = endConsumerAddresses.filter((address) => address.isAllowed);
        const isMethodRadioButtonSelected = this.findElement(
            `${this.METHOD_RADIO_SELECTOR}${this.CHECKED_SELECTOR}`
        );

        if (!allowedAddresses.length && isMethodRadioButtonSelected) {
            this._clearDeliveryAddressSelection();
            this._toggleCheckoutButtonEnabled(false);
        } else {
            const selectedAddress = this.find(
                `${this.ADDRESS_ITEM_SELECTOR} ${this.INPUT_SELECTOR}${this.CHECKED_SELECTOR}`
            );
            if (isMethodRadioButtonSelected && selectedAddress.length) {
                this._toggleCheckoutButtonEnabled(true);
            }
        }
    }

    private _clearDeliveryAddressSelection(): void {
        this._fetchService.post(this._clearDeliveryAddressUrl).then(
            () => {
                this._logger.info("Clear delivery address posted successfully.");
            },
            (error) => {
                this._logger.error("Clear delivery address post failed - %s", error);
            }
        );
    }

    private _forceDisplayInvalidAddresses(
        endConsumerAddresses: IMultipleCouriersAddressModel[]
    ): void {
        if (!endConsumerAddresses.length) {
            return;
        }

        const addressesWrapper = this.findElement(`${this.ADDRESSES_WRAPPER_SELECTOR}`);
        if (!addressesWrapper) {
            return;
        }

        let isOtherComponentVisible = false;
        const allowedAddresses = endConsumerAddresses.filter((x) => x.isAllowed);
        if (allowedAddresses.length) {
            addressesWrapper.removeAttribute(this.ACTIVE_HIDDEN_DATA_ATTRIBUTE);
            addressesWrapper.setAttribute(this.ACTIVE_SHOWN_DATA_ATTRIBUTE, "");
            isOtherComponentVisible = true;
        } else {
            addressesWrapper.removeAttribute(this.ACTIVE_SHOWN_DATA_ATTRIBUTE);
            addressesWrapper.setAttribute(this.ACTIVE_HIDDEN_DATA_ATTRIBUTE, "");
        }
        this._toggleDeliveryCommentVisibility(isOtherComponentVisible);
        this._toggleNotificationsPhonesVisibility(isOtherComponentVisible);
    }

    private _toggleDeliveryCommentVisibility(isVisible: boolean): void {
        const deliveryComment = this.findElement(`${this.DELIVERY_COMMENT_WRAPPER_SELECTOR}`);
        if (deliveryComment) {
            Utils.toggleClass(deliveryComment, this.CLASS_HIDDEN, !isVisible);
        }
    }

    private _toggleNotificationsPhonesVisibility(isVisible: boolean): void {
        if (this._deliveryNotificationsPhone) {
            const notificationsPhonesComponent = this.findElement(
                `${this.DELIVERY_NOTIFICATIONS_PHONES_SELECTOR}`
            );
            if (notificationsPhonesComponent) {
                Utils.toggleClass(notificationsPhonesComponent, this.CLASS_HIDDEN, !isVisible);
            }
        }
    }
}
